export type StatusWithinWindow = 'nonCompliant' | 'partial' | 'compliant' | 'pending';

export interface WorkOrdersComplianceSettings {
  rightGrease: boolean;
  rightAmount: boolean;
  rightTime: boolean;
}

export const STATUS_ORDER: StatusWithinWindow[] = ['nonCompliant', 'partial', 'compliant', 'pending'];

/** Bar segment fill / stroke — shared by the chart series and on-page legend. */
export const WORK_ORDERS_STATUS_DISPLAY_COLORS: Record<StatusWithinWindow, { fill: string; stroke: string }> = {
  compliant: { fill: '#92D05088', stroke: '#6ea235' },
  partial: { fill: '#eab30888', stroke: '#a16207' },
  nonCompliant: { fill: '#dc262688', stroke: '#991b1b' },
  pending: { fill: '#3B82F666', stroke: '#2563EB' },
};

/** Order for the on-page status legend (compliant → critical). */
export const WORK_ORDERS_LEGEND_STATUS_ORDER: StatusWithinWindow[] = [
  'compliant',
  'partial',
  'nonCompliant',
  'pending',
];

/** Compliance window outline colors on the main chart — match `createWindowBorder` in WorkOrdersApi. */
export const WORK_ORDERS_WINDOW_BORDER_STROKE = {
  active: '#6ea235',
  inactive: '#808080',
} as const;

/** Late region outline — dashed grey, to read as "overflow" of the adjacent window. */
export const WORK_ORDERS_LATE_BORDER_STROKE = '#9ca3af';
/** Late pending fill / stroke — soft purple, to set late (still-not-done) pending apart from on-time pending (blue). */
export const WORK_ORDERS_LATE_PENDING_COLOR = { fill: '#a855f75a', stroke: '#9333ea' };

export interface ActionEvent {
  occurred?: string;
  volumeDispensed?: number;
  volumeDelivered?: number;
  rfid?: string;
  deviceName?: string;
  [key: string]: unknown;
}

/**
 * A maintenance Cause Code assignable to a task (e.g. MS-C1 — Machine Out of Service).
 * `code` is the stable string key exchanged with the backend (empty string = none);
 * `description` is the translated, human-readable label.
 */
export interface CauseCode {
  code: string;
  description: string;
}

export interface ActionItem {
  /** Stable per-task id from the backend; used for selection and Cause Code editing. */
  actionId: string;
  causeCode: string;
  /** Free-text comment for the task; exchanged with the backend ('' = none). */
  comment?: string;
  pointName: string;
  rightGrease: boolean;
  rightAmount: boolean;
  rightTime: boolean;
  compliant: boolean;
  eventCount: number;
  volumeRequirement: number;
  volumeTolerance: number;
  cumulativeVolumeDelivered: number;
  workOrderNumber?: string;
  events?: ActionEvent[];
  /**
   * GreaseAction.Status when the API sends it (string or enum int). Active => Pending tab.
   */
  status?: string | number;
  /** When true, this action belongs in the Pending tab (if the API sends an explicit flag). */
  pending?: boolean;
  /** Backend flag: task greased late, or still pending past the original window. False = on-time. */
  isLate?: boolean;
}

/**
 * Stable key for selecting/editing a task in the detail drawer.
 * Prefers the backend `taskId`; falls back to pointName+index until the backend ships it.
 */
export function getActionKey(action: ActionItem, index: number): string {
  return action.actionId ?? `${action.pointName}-${index}`;
}

/** Grouping for Work Orders detail panel tabs: compliant, failed, in-progress, or no events yet. */
export type WorkOrdersActionDetailTab = 'compliant' | 'nonCompliant' | 'pending' | 'partial';

/**
 * Whether this row must appear in the Pending tab (before compliant / partial / non-compliant rules).
 * Matches backend: greasing task still Active; optional fallback when status is omitted and the window is active.
 */
function goesToPendingTab(a: ActionItem, windowActive: boolean): boolean {
  if (a.pending === true) return true;

  const s = a.status;
  if (s !== undefined && s !== null) {
    if (typeof s === 'number') {
      if (s === 0) return true;
      if (s === 1) return false;
      return false;
    }
    const t = String(s).trim().toLowerCase();
    if (t === 'active' || t === 'pending') return true;
    if (t === 'inactive') return false;
    return false;
  } else if (a.eventCount === 0 && windowActive) {
    return true;
  }

  return false;
}

/**
 * Tab for a point in the detail drawer — matches backend
 * `CalculateScheduleCounts`: Pending first; then same useRight* dimensions for closed actions with events;
 * closed with no events => non-compliant unless routed to Pending above.
 */
export function categorizeWorkOrdersActionForDetailTab(
  a: ActionItem,
  compliance?: WorkOrdersComplianceSettings,
  windowActive = false
): WorkOrdersActionDetailTab {
  if (goesToPendingTab(a, windowActive)) return 'pending';

  if (a.eventCount === 0) return 'nonCompliant';

  const useGrease = compliance?.rightGrease !== false;
  const useAmount = compliance?.rightAmount !== false;
  const useTime = compliance?.rightTime !== false;

  const isCompliant =
    (!useGrease || a.rightGrease) && (!useAmount || a.rightAmount) && (!useTime || a.rightTime);

  const allSelectedDimensionsFailed =
    (useGrease && !a.rightGrease) &&
    (useAmount && !a.rightAmount) &&
    (useTime && !a.rightTime);

  if (isCompliant) return 'compliant';
  if (allSelectedDimensionsFailed) return 'nonCompliant';
  return 'partial';
}

/** POST `api/report/compliance/site/actions/gantt` request body (JSON camelCase) */
export interface WorkOrdersReportRequest {
  startDate?: string;
  endDate?: string;
  machineIds: string[];
  scheduleIds?: string[];
  groupBy?: 'AssetAndSchedule' | 'Schedule';
  compliance: {
    useRightGrease: boolean;
    useRightAmount: boolean;
    useRightTime: boolean;
  };
}

/** PATCH `api/report/compliance/site/actions/cause-codes` — one task's cause code change (empty string clears). */
export interface CauseCodeUpdate {
  actionId: string;
  causeCode: string;
  /** Free-text comment ('' clears). */
  comment: string;
}

/** One compliance time window (segment of a schedule) — matches API / mock child objects */
export interface ComplianceWindowRow {
  index: string | number;
  startDateUtc: string;
  /** Full end of the work order — the right edge of the late (dashed) region when late; else the window end. */
  endDateUtc: string;
  /**
   * Original scheduled deadline: end of the solid (on-time) bar and start of the dashed (late) bar.
   * `null`/absent = not late — the solid bar spans `startDateUtc → endDateUtc` and there is no late region.
   */
  originalEndDateUtc?: string | null;
  /** Backend flag: work order is overdue / has a late window. Authoritative late signal. */
  isLate?: boolean;
  active: boolean;
  expected: number;
  /** On-time counts (solid bar: `startDateUtc → originalEndDateUtc`). */
  schedule: Record<StatusWithinWindow, number>;
  /**
   * Late counts (dashed bar: `originalEndDateUtc → endDateUtc`). `null` when not late.
   * `compliant` is always 0 — right-time always fails in the late window.
   */
  lateSchedule?: Record<StatusWithinWindow, number> | null;
  unschedule?: number;
  actions: ActionItem[];
}

export interface UnscheduleEventPoint {
  pointName: string;
  occurred?: string;
  volumeDispensed?: number;
  volumeDelivered?: number;
  rfid?: string;
  deviceName?: string;
}

export interface UnscheduleEventGroup {
  index: string;
  occurred: string;
  events: UnscheduleEventPoint[];
}

/**
 * One machine + schedule: the chart uses one Y row; {@link workOrders} are periods on that row.
 * {@link unscheduleEvents} are greasing sessions that fall outside every compliance window.
 */
export interface ApiScheduleGroupRow {
  index: string | number;
  assetId: string | null;
  assetName?: string | null;
  scheduleName?: string | null;
  unscheduleEvents?: UnscheduleEventGroup[];
  workOrders: ComplianceWindowRow[];
}

/**
 * Legacy flat shape (one window). Normalized into {@link ApiScheduleGroupRow} for the chart.
 */
export interface ApiScheduleRow {
  index: string | number;
  /** Present when this row was expanded from {@link ApiScheduleGroupRow} */
  parentScheduleIndex?: number;
  assetId: string;
  assetName?: string;
  scheduleName?: string;
  startDateUtc: string;
  endDateUtc: string;
  active: boolean;
  expected: number;
  schedule: Record<StatusWithinWindow, number>;
  unschedule: number;
  actions: ActionItem[];
}

export type WorkOrdersInputItem = ApiScheduleGroupRow | ApiScheduleRow;

export type WorkOrdersInput = WorkOrdersInputItem[];

function legacyFlatRowToGroup(row: ApiScheduleRow): ApiScheduleGroupRow {
  return {
    index: row.parentScheduleIndex ?? 0,
    assetId: row.assetId,
    assetName: row.assetName,
    scheduleName: row.scheduleName,
    workOrders: [
      {
        index: row.index,
        startDateUtc: row.startDateUtc,
        endDateUtc: row.endDateUtc,
        active: row.active,
        expected: row.expected,
        schedule: row.schedule,
        unschedule: row.unschedule,
        actions: row.actions,
      },
    ],
  };
}

export function isApiScheduleGroupRow(row: WorkOrdersInputItem): row is ApiScheduleGroupRow {
  return Array.isArray((row as ApiScheduleGroupRow).workOrders);
}

/** One chart row per asset+schedule; groups already from API, legacy flat rows wrapped in one window. */
export function toWorkOrdersGroupRows(
  input: WorkOrdersInput | readonly WorkOrdersInputItem[]
): ApiScheduleGroupRow[] {
  const out: ApiScheduleGroupRow[] = [];
  for (const item of input) {
    if (isApiScheduleGroupRow(item)) {
      out.push(item);
    } else {
      out.push(legacyFlatRowToGroup(item));
    }
  }
  return out;
}

export function getWorkOrdersGroupYLabelLines(
  group: ApiScheduleGroupRow,
  groupBy: 'AssetAndSchedule' | 'Schedule' = 'AssetAndSchedule'
): string[] {
  const scheduleLabel = group.scheduleName?.trim() || '';
  // Schedule mode merges all assets under a schedule — no asset dimension, so show a single schedule line.
  if (groupBy === 'Schedule') {
    return [scheduleLabel || group.index.toString()];
  }
  const assetLabel = group.assetName?.trim() || group.assetId || group.index.toString();
  return [assetLabel, scheduleLabel];
}

export interface ScheduleWindowInternal {
  index: string | number;
  assetId: string | null;
  row: number;
  start: number;
  end: number;
  active: boolean;
  expected: number;
  unschedule: number;
}

export interface SegmentInternal {
  index: string | number;
  assetId: string | null;
  row: number;
  start: number;
  end: number;
  value: number;
  status: StatusWithinWindow;
}

export interface UnscheduleClickPayload {
  assetId: string | null;
  assetName?: string;
  scheduleName?: string;
  row: number;
  unscheduleEvents: UnscheduleEventGroup[];
}

export interface ScheduleClickPayload {
  /** Window id from API (e.g. "0.1"); not the drawer list index */
  complianceWindowIndex: string | number;
  assetId: string | null;
  scheduleName?: string;
  assetName?: string;
  row: number;
  active: boolean;
  /** Work order is overdue (past its original deadline). Drives the "Late" header tag. */
  isOverdue: boolean;
  expected: number;
  unschedule: number;
  windowStart: Date;
  windowEnd: Date;
  actions: ActionItem[];
}
